/**
 * Markup Store Slice
 *
 * Zustand slice for application markup state management.
 * Handles fetching and managing markup percentages for applications.
 */

import { clearError as clearOpError, hasOperationLoaded, isOperationLoading } from '@gw/ui';

import { createAsyncSliceAction } from '@/stores/helpers/asyncSliceAction';

import type { ErrorStates, ErrorStatuses, LoadedTimestamps, LoadingStates } from '@gw/ui/types';
import type { StateCreator } from 'zustand';

// ============================================================================
// Types
// ============================================================================

export interface MarkupData {
  applicationId: string;
  markupPercent: number;
}

// ============================================================================
// Slice Interface
// ============================================================================

export interface MarkupSlice {
  // State properties
  markups: Record<string, MarkupData>;
  isLoading: LoadingStates;
  hasLoaded: LoadingStates;
  lastLoaded: LoadedTimestamps;
  errors: ErrorStates;
  errorStatuses: ErrorStatuses;

  // Actions
  fetchApplicationMarkup: (applicationId: string) => Promise<number>;
  clearError: (operation?: string) => void;
}

// ============================================================================
// Slice Implementation
// ============================================================================

export const createMarkupSlice: StateCreator<MarkupSlice> = (set, get) => ({
  // Initial state
  markups: {},
  isLoading: {},
  hasLoaded: {},
  lastLoaded: {},
  errors: {},
  errorStatuses: {},

  // Fetch application markup
  fetchApplicationMarkup: async (applicationId: string) => {
    const operation = 'fetchApplicationMarkup';

    // Check if already loading
    if (isOperationLoading(get().isLoading, operation)) {
      const cached = get().markups[applicationId];
      return cached?.markupPercent ?? 35; // Default 35% markup
    }

    // Check if already loaded and not stale (5 minutes)
    if (hasOperationLoaded(get().hasLoaded, operation)) {
      const lastLoaded = get().lastLoaded[operation];
      const now = Date.now();
      if (lastLoaded && now - lastLoaded < 5 * 60 * 1000) {
        const cached = get().markups[applicationId];
        if (cached) {
          return cached.markupPercent;
        }
      }
    }

    await createAsyncSliceAction<MarkupSlice, number>(
      operation,
      async () => {
        // TODO: Replace with actual service call when implemented
        // In the future, this would be: return await markupApi.getApplicationMarkup(applicationId);
        return 35;
      },
      (markupPercent) => ({
        markups: {
          ...get().markups,
          [applicationId]: {
            applicationId,
            markupPercent,
          },
        },
      }),
      set,
      get
    )();

    // Return markup from state, or default 35% on error
    const cached = get().markups[applicationId];
    return cached?.markupPercent ?? 35;
  },

  // Clear error
  clearError: (operation?: string) => {
    const currentState = get();
    if (operation) {
      set({ errors: clearOpError(currentState.errors, operation) });
    } else {
      set({ errors: {} });
    }
  },
});
